import { OskCurrencyPipe } from '@shared/pipes/osk-currency.pipe';
import { Component, computed, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AdminOrderStore } from '@features/admin';

@Component({
  selector: 'app-admin-orders',
  standalone: true,
  imports: [CommonModule, FormsModule, OskCurrencyPipe],
  templateUrl: 'admin-orders.component.html',
  styleUrl: 'admin-orders.component.scss',
})
export class AdminOrdersComponent implements OnInit {
  readonly store = inject(AdminOrderStore);

  searchTerm = '';
  statusFilter = '';
  restaurantFilter = '';
  dateFilter = '';
  selectedOrder: any = null;

  // Computed signals
  filteredOrders = computed(() => {
    let orders = this.store.orders();

    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      orders = orders.filter(
        (o) =>
          o.orderNumber?.toLowerCase().includes(term) ||
          o.customerName?.toLowerCase().includes(term),
      );
    }

    return orders;
  });

  restaurants = computed(() => {
    const uniqueRestaurants = new Map();
    this.store.orders().forEach((order) => {
      if (order.restaurantId && order.restaurantName) {
        uniqueRestaurants.set(order.restaurantId, {
          id: order.restaurantId,
          name: order.restaurantName,
        });
      }
    });
    return Array.from(uniqueRestaurants.values());
  });

  pendingCount = computed(
    () => this.store.orders().filter((o) => o.status === 'PENDING').length,
  );

  preparingCount = computed(
    () => this.store.orders().filter((o) => o.status === 'PREPARING').length,
  );

  readyCount = computed(
    () => this.store.orders().filter((o) => o.status === 'READY').length,
  );

  deliveredCount = computed(
    () => this.store.orders().filter((o) => o.status === 'DELIVERED').length,
  );

  ngOnInit(): void {
    this.store.loadOrders();
  }

  onSearch(term: string): void {
    // Le filtrage est géré par le computed signal
  }

  onFilterChange(): void {
    this.store.loadOrders({
      status: this.statusFilter || undefined,
      restaurantId: this.restaurantFilter
        ? Number(this.restaurantFilter)
        : undefined,
      date: this.dateFilter || undefined,
    });
  }

  refreshOrders(): void {
    this.store.loadOrders();
  }

  viewOrderDetails(order: any): void {
    this.selectedOrder = order;
    this.store.loadOrderDetails(order.id);
  }

  closeOrderDetails(): void {
    this.selectedOrder = null;
  }

  updateOrderStatus(order: any, newStatus: string): void {
    if (
      confirm(
        `Voulez-vous changer le statut de la commande #${order.orderNumber} à "${this.getStatusLabel(newStatus)}" ?`,
      )
    ) {
      this.store.updateOrderStatus({ orderId: order.id, status: newStatus });
    }
  }

  cancelOrder(order: any): void {
    const reason = prompt("Raison de l'annulation :");
    if (reason) {
      this.store.cancelOrder({ orderId: order.id, reason });
    }
  }

  exportOrders(): void {
    // Export CSV
    const orders = this.filteredOrders();
    const csv = this.generateCSV(orders);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `commandes_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  }

  printOrder(order: any): void {
    window.print();
  }

  goToPage(page: number): void {
    if (page >= 0 && page < this.store.totalPages()) {
      this.store.loadOrders({ page });
    }
  }

  getPageNumbers(): number[] {
    const total = this.store.totalPages();
    const current = this.store.currentPage();
    const pages: number[] = [];

    const start = Math.max(0, current - 2);
    const end = Math.min(total - 1, current + 2);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      PENDING: 'En attente',
      CONFIRMED: 'Confirmée',
      PREPARING: 'En préparation',
      READY: 'Prête',
      DELIVERING: 'En livraison',
      DELIVERED: 'Livrée',
      CANCELLED: 'Annulée',
    };
    return labels[status] || status;
  }

  private generateCSV(orders: any[]): string {
    const headers = [
      'Numéro',
      'Date',
      'Client',
      'Restaurant',
      'Total',
      'Statut',
    ];
    const rows = orders.map((o) => [
      o.orderNumber,
      new Date(o.createdAt).toLocaleString('fr-FR'),
      o.customerName || 'N/A',
      o.restaurantName,
      o.totalAmount,
      this.getStatusLabel(o.status),
    ]);

    return [headers.join(';'), ...rows.map((r) => r.join(';'))].join('\n');
  }
}
